import {
  faChevronDown,
  faExclamationCircle,
  faFloppyDisk,
  faPencil,
  faUserCheck
} from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import clsx from 'clsx'
import { useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import z from 'zod'
import { useOrderApplicantStatusMutation } from '~/api/mutations/order'
import { useAccountingOrderData } from '~/api/queries/order'
import {
  Checkbox,
  DateInput,
  Dropdown,
  Form,
  Submit,
  TextInput
} from '~/components/Form'
import { formClass } from '~/components/Form/Form.css'
import { InputLine } from '~/components/Form/InputLine'
import { StandardModal } from '~/components/Global/Design/Modal/StandardModal'
import { Loader } from '~/components/Global/Elements/Loader/Loader'
import { Alert, AlertVariation } from '~/components/Global/Interactions/Alert'
import { ButtonVariation } from '~/components/Global/Interactions/Button'
import { ToastType } from '~/components/Global/Interactions/Toast/ToastType'
import { AccountingErrorCode } from '~/domains/accounting/errors'
import type { ApplicantHeadData } from '~/domains/applicants/types'
import { useConfig } from '~/domains/configuration/queries/config'
import type { AcceptModalOrder } from '~/domains/orders/types'
import { useTaxonomy } from '~/domains/taxonomies/queries/useTaxonomy'
import { useActiveAgency } from '~/domains/users/hooks/useActiveAgency'
import { toast } from '~/utils'
import { launchFireworks } from '~/utils/fireworks'
import ArmadoLogo from '../../../../../img/armado-logo.svg'
import {
  accountingWarning,
  accountingWarningsContent,
  accountingWarningsHeader,
  accountingWarningsWrapper,
  accountingWrapper,
  displayed,
  hidden,
  inlineList,
  inlineListItem,
  inlineListItemAction,
  optionItemChevron,
  serviceHeader,
  serviceLogo
} from '../AccountingInputs.css'
import { acceptModalRef, closeOrderModalRef } from './modals'
import { TempoSyncPreviewItem } from './TempoSyncPreviewItem'

type SubmitParams = {
  startDate: Date
  unavailabilityType: string
  endDate?: Date | null
  accountingAgencyId?: string
  accountingContactId?: string
  accountingExternalQualificationId?: string
  accountingSendActivationMail?: boolean
  accountingWarnClient?: boolean
}

const createFormSchema = (
  requireQualification: boolean,
  requireEndDate: boolean
) => {
  return z
    .object({
      startDate: z.coerce.date({
        error: () => 'Veuillez renseigner une date de début valide'
      }),
      endDate: z.coerce
        .date({ error: () => 'Veuillez renseigner une date de fin valide' })
        .nullable()
        .optional(),
      unavailabilityType: z.string({
        error: () => "Veuillez sélectionner un type d'indisponibilité"
      }),
      accountingExternalQualificationId: requireQualification
        ? z.string({
            error: () => 'Veuillez sélectionner une qualification'
          })
        : z.string().optional(),
      accountingAgencyId: z.string().optional(),
      accountingContactId: z.string().optional(),
      accountingSendActivationMail: z.boolean().optional(),
      accountingWarnClient: z.boolean().optional()
    })
    .refine((data) => !data.endDate || data.startDate < data.endDate, {
      message: 'La date de début doit être antérieure à la date de fin',
      path: ['endDate']
    })
    .refine((data) => !requireEndDate || !!data.endDate, {
      message: 'La date de fin est obligatoire',
      path: ['endDate']
    })
}

export const AcceptModal = () => {
  const [show, setShow] = useState<boolean>(false)
  const resolveRef = useRef<(v: boolean) => void>(() => {})
  const [order, setOrder] = useState<AcceptModalOrder>()
  const [applicants, setApplicants] = useState<(ApplicantHeadData | number)[]>(
    []
  )
  const [statusId, setStatusId] = useState<number>()
  const { mutate: updateApplicantStatusMutate, isPending } =
    useOrderApplicantStatusMutation(order?.id)
  const { data: unavailabilityTypes } = useTaxonomy('unavailability_types')
  const { data: accountingService } = useConfig(
    'organization.accounting_service'
  )
  const { activeAgency } = useActiveAgency()
  const { data: mandatoryContractDates } = useConfig(
    'mandatory_order.contract_dates'
  )
  // Armado inputs display states
  const [showContactDropdown, setShowContactDropdown] = useState<boolean>(false)
  const [reduceAccountingWarnings, setReduceAccountingWarnings] =
    useState<boolean>(true)
  const [armadoContactName, setArmadoContactName] = useState<string>()

  acceptModalRef.current = async (
    order: AcceptModalOrder,
    applicants: (ApplicantHeadData | number)[],
    statusId: number
  ) => {
    if (
      order.applicantWantedCount !== 0 &&
      order.applicantAcceptedCount + applicants.length >
        order.applicantWantedCount
    ) {
      void toast(
        'Trop de candidats acceptés',
        `Le nombre maximum de candidats pour cette commande sera dépassé en acceptant ${applicants.length > 1 ? 'ceux-ci' : 'celui-ci'} (actuellement ${order.applicantAcceptedCount} accepté${order.applicantAcceptedCount > 1 ? 's' : ''} sur ${order.applicantWantedCount} souhaité${order.applicantWantedCount > 1 ? 's' : ''})`,
        ToastType.Warning
      )
      return false
    }
    return await new Promise((resolve) => {
      setShow(true)
      setOrder(order)
      setApplicants(applicants)
      setStatusId(statusId)
      resolveRef.current = resolve
    })
  }

  const {
    data: accountingData,
    isError: isAccountingDataError,
    error: accountingError,
    isFetching: isAccountingDataLoading
  } = useAccountingOrderData(
    order?.id ?? 0,
    !!accountingService,
    applicants.length === 1
      ? typeof applicants[0] === 'number'
        ? applicants[0]
        : applicants[0]?.id
      : undefined,
    activeAgency?.id
  )

  const isAccountingAvailable =
    !!accountingService &&
    !isAccountingDataError &&
    !!accountingData?.externalData
  const accountingOrderData = accountingData ?? null
  const externalData = accountingOrderData?.externalData
  const canSendOrder = externalData?.canSendOrder ?? true
  const requireQualification =
    isAccountingAvailable &&
    !accountingData?.externalData?.externalQualificationId &&
    (accountingData?.externalData?.externalQualifications?.length ?? 0) > 0
  const requireEndDate =
    Boolean(mandatoryContractDates) &&
    (order?.contractTerm?.hasFixedDuration ?? true)
  const schema = createFormSchema(requireQualification, requireEndDate)
  const defaultStartDate = order?.contractStart?.match(/^\d{4}-\d{2}-\d{2}/)
    ? new Date(order.contractStart)
    : undefined
  const defaultEndDate = order?.contractEnd?.match(/^\d{4}-\d{2}-\d{2}/)
    ? new Date(order.contractEnd)
    : undefined
  const agency = externalData?.agencyId
    ? externalData?.agencies?.find(
        (agency) => agency.id === externalData.agencyId
      )
    : externalData?.agencies?.[0]
  const contact = externalData?.contacts?.find(
    (contact) =>
      contact.id ===
      (externalData.interlocutorContactId ?? externalData.contactId)
  )
  const qualification = externalData?.externalQualificationId
    ? externalData?.externalQualifications?.find(
        (qualif) => qualif.id === externalData.externalQualificationId
      )
    : null

  const metadata = JSON.parse(externalData?.externalMetadata || '{}') as {
    [key: string]: any
  }

  const willCompleteOrder =
    order &&
    order.applicantWantedCount !== 0 &&
    order.applicantAcceptedCount + applicants.length >=
      order.applicantWantedCount

  const onConfirm = (data: SubmitParams) => {
    if (!order || !applicants || !statusId) {
      resolveRef.current(false)
      setShow(false)
      return
    }

    if (isAccountingDataLoading) {
      return
    }

    updateApplicantStatusMutate(
      {
        applicants: applicants,
        status: statusId,
        endDate: data.endDate || undefined,
        startDate: data.startDate,
        unavailabilityType: Number.parseInt(data.unavailabilityType, 10),
        accountingData: isAccountingDataError
          ? undefined
          : {
              agencyId: data.accountingAgencyId
                ? Number.parseInt(data.accountingAgencyId, 10)
                : undefined,
              contactId: data.accountingContactId
                ? Number.parseInt(data.accountingContactId, 10)
                : undefined,
              externalQualificationId: data.accountingExternalQualificationId
                ? Number.parseInt(data.accountingExternalQualificationId, 10)
                : undefined,
              externalOtherData: JSON.stringify({
                sendActivationEmail: data.accountingSendActivationMail,
                warnClient: data.accountingWarnClient
              })
            }
      },
      {
        onSuccess: () => {
          setShow(false)
          resolveRef.current(true)
          launchFireworks()
          toast(
            `Candidat${applicants.length > 1 ? 's' : ''} accepté${
              applicants.length > 1 ? 's' : ''
            }`,
            `Le${applicants.length > 1 ? 's' : ''} candidat${
              applicants.length > 1 ? 's' : ''
            } ${applicants.length > 1 ? 'ont' : 'a'} été accepté${
              applicants.length > 1 ? 's' : ''
            } sur la commande`,
            ToastType.Success
          )
          // Open close order modal if order is now fully filled willCompleteOrder && closeOrderModalRef.current is defined
          if (willCompleteOrder && closeOrderModalRef.current) {
            setTimeout(() => {
              closeOrderModalRef.current()
            }, 500)
          }
        },
        onError: () => {
          setShow(false)
          resolveRef.current(false)
        }
      }
    )
  }
  const onCancel = () => {
    if (isPending) return
    resolveRef.current(false)
    setShow(false)
    setShowContactDropdown(false)
  }
  if (!show) {
    // The form has to be unmounted on modal close to remove its state
    return null
  }

  return createPortal(
    <Form
      onSubmit={onConfirm}
      schema={schema}
      defaultValues={{
        startDate: defaultStartDate,
        endDate: defaultEndDate
      }}
    >
      <StandardModal
        open={show}
        onClose={onCancel}
        title="Spécifier les dates du poste de la commande"
        icon={faUserCheck}
        buttons={[
          <Submit
            fullWidth={true}
            loadingText={
              isPending ? 'Synchronisation en cours...' : 'Enregistrement...'
            }
            buttonVariation={ButtonVariation.Primary}
            isSubmitting={isPending}
            isSubmitSuccessful={false}
            key="submit"
          >
            <FontAwesomeIcon icon={faFloppyDisk} />
            Enregistrer
          </Submit>
        ]}
      >
        <div className={formClass}>
          <InputLine>
            <DateInput
              label="Début"
              name="startDate"
              startName="startDate"
              endName="endDate"
              showTime
              required
              fullWidth={order?.contractTerm?.hasFixedDuration ?? true}
            />
            {(order?.contractTerm?.hasFixedDuration ?? true) && (
              <DateInput
                label="Fin"
                name="endDate"
                startName="startDate"
                endName="endDate"
                showTime
              />
            )}
          </InputLine>
          <Dropdown
            options={
              unavailabilityTypes?.map((type) => ({
                label: type.name,
                value: String(type.id)
              })) ?? []
            }
            required
            label="Type d'indisponibilité"
            name="unavailabilityType"
          />
          {willCompleteOrder && (
            <Alert
              message={`En acceptant ${applicants.length === 1 ? 'ce' : 'ces'} candidat${applicants.length > 1 ? 's' : ''}, le nombre de recrutements souhaités pour cette commande (${order.applicantWantedCount}) sera atteint.`}
              variation={AlertVariation.Info}
            />
          )}
        </div>

        {/** Accounting form section */}
        {!!accountingService && (
          <div className={accountingWrapper}>
            {accountingService === 'Armado' ? (
              <img
                src={ArmadoLogo}
                alt="Armado"
                title="Armado"
                className={serviceLogo}
              />
            ) : (
              <div className={serviceHeader}>{accountingService}</div>
            )}
            {!isAccountingDataError && !externalData && (
              <Loader small={true} forceText={true} />
            )}
            {isAccountingDataError && (
              <div className={accountingWarningsHeader}>
                <FontAwesomeIcon icon={faExclamationCircle} />
                <span>
                  {[
                    accountingError?.response?.message ??
                      'Une erreur est survenue lors du chargement des informations nécessaires.',
                    accountingError?.response?.code ===
                    AccountingErrorCode.NotConfiguredForAgency
                      ? "Votre agence ne permet pas l'envoi de la commande vers votre logiciel comptable."
                      : null
                  ]
                    .filter(Boolean)
                    .join(' ')}
                </span>
              </div>
            )}
            {!isAccountingDataError && order && externalData && (
              <>
                {accountingService === 'Armado' &&
                  (!!accountingOrderData.warnings.order?.length ||
                    !!accountingOrderData.warnings.applicant?.length) && (
                    <div
                      className={accountingWarningsWrapper}
                      onClick={() =>
                        setReduceAccountingWarnings(!reduceAccountingWarnings)
                      }
                    >
                      <div className={accountingWarningsHeader}>
                        <FontAwesomeIcon icon={faExclamationCircle} />
                        <span>
                          Des données manquantes devront être renseignées pour
                          l&apos;envoi vers {accountingService}, ultérieurement
                        </span>
                        <FontAwesomeIcon
                          icon={faChevronDown}
                          className={clsx(
                            optionItemChevron,
                            !reduceAccountingWarnings ? 'expanded' : null
                          )}
                        />
                      </div>
                      <div
                        className={clsx(
                          accountingWarningsContent,
                          reduceAccountingWarnings ? hidden : null
                        )}
                      >
                        {!!accountingOrderData.warnings.order?.length &&
                          accountingOrderData.warnings.order.map((warning) => (
                            <div
                              key={warning.replace(' ', '_')}
                              className={accountingWarning}
                            >
                              {warning}
                            </div>
                          ))}
                        {canSendOrder === false && (
                          <div className={accountingWarning}>
                            La commande Armado ne sera pas créée, le module
                            "commande" n'étant pas activée pour votre agence.
                          </div>
                        )}
                        {!!accountingOrderData.warnings.applicant?.length &&
                          accountingOrderData.warnings.applicant.map(
                            (warning) => (
                              <div
                                key={warning.replace(' ', '_')}
                                className={accountingWarning}
                              >
                                {warning}
                              </div>
                            )
                          )}
                      </div>
                    </div>
                  )}

                {accountingService === 'Armado' && (
                  <div className={inlineList}>
                    <div className={inlineListItem}>
                      Agence : <strong>{agency?.name}</strong>
                    </div>
                    <div className={inlineListItem}>
                      Contact / Interlocuteur:{' '}
                      <strong>{armadoContactName ?? contact?.name}</strong>
                      {!externalData.interlocutorContactId &&
                        externalData.contacts.length > 1 && (
                          <span
                            className={inlineListItemAction}
                            onClick={() =>
                              setShowContactDropdown(!showContactDropdown)
                            }
                          >
                            <FontAwesomeIcon icon={faPencil} />
                          </span>
                        )}
                    </div>
                  </div>
                )}
                {accountingService === 'Tempo' && (
                  <div className={inlineList}>
                    <TempoSyncPreviewItem
                      label="Commande :"
                      statusText={
                        externalData.syncStatus === 'synced'
                          ? 'déjà synchronisée · sera mise à jour'
                          : 'sera créée dans Tempo'
                      }
                      preview={
                        externalData.syncStatus !== 'synced'
                          ? externalData.orderPreview
                          : null
                      }
                    />
                    {applicants.length === 1 && (
                      <TempoSyncPreviewItem
                        label={`Candidat ${
                          typeof applicants[0] !== 'number'
                            ? `${applicants[0]?.firstname ?? ''} ${applicants[0]?.lastname ?? ''}`.trim()
                            : ''
                        } :`}
                        statusText={
                          externalData.applicantSyncStatus === 'synced'
                            ? 'déjà synchronisé · sera mis à jour'
                            : 'sera créé dans Tempo'
                        }
                        preview={
                          externalData.applicantSyncStatus !== 'synced'
                            ? externalData.applicantPreview
                            : null
                        }
                      />
                    )}
                    <TempoSyncPreviewItem
                      label={`Contact${externalData.contactName ? ` ${externalData.contactName}` : ''} :`}
                      statusText={
                        externalData.contactSyncStatus === 'synced'
                          ? 'déjà synchronisé · sera mis à jour'
                          : 'sera créé dans Tempo'
                      }
                      preview={
                        externalData.contactSyncStatus !== 'synced'
                          ? externalData.contactPreview
                          : null
                      }
                    />
                    <TempoSyncPreviewItem
                      label={`Entreprise${externalData.companyName ? ` ${externalData.companyName}` : ''} :`}
                      statusText={
                        externalData.companySyncStatus === 'synced'
                          ? 'déjà synchronisée · sera mise à jour'
                          : 'sera créée dans Tempo'
                      }
                      preview={
                        externalData.companySyncStatus !== 'synced'
                          ? externalData.companyPreview
                          : null
                      }
                    />
                  </div>
                )}
              </>
            )}
            {qualification && (
              <div className={inlineList}>
                <div className={inlineListItem}>
                  Qualification : <strong>{qualification.name}</strong>
                </div>
              </div>
            )}

            {externalData &&
              (externalData.agencyId != null ||
                externalData.agencies[0]?.id != null) && (
                <TextInput
                  type="hidden"
                  name="accountingAgencyId"
                  value={externalData.agencyId ?? externalData.agencies[0]?.id}
                />
              )}
            {externalData &&
              (typeof externalData.interlocutorContactId === 'number' ||
                externalData.contacts.length === 1) && (
                <TextInput
                  type="hidden"
                  name="accountingContactId"
                  value={
                    externalData.interlocutorContactId ??
                    externalData.contacts[0]?.id
                  }
                />
              )}
            {externalData &&
              typeof externalData.externalQualificationId === 'number' && (
                <TextInput
                  type="hidden"
                  name="accountingExternalQualificationId"
                  value={externalData.externalQualificationId}
                />
              )}

            {externalData &&
              !externalData.interlocutorContactId &&
              externalData.contacts.length > 1 && (
                <Dropdown
                  label={
                    showContactDropdown ? 'Contact / Interlocuteur' : undefined
                  }
                  name="accountingContactId"
                  wrapperClassName={showContactDropdown ? displayed : hidden}
                  placeholder="Sélectionner un contact"
                  options={externalData.contacts.map((contact) => ({
                    label: contact.name,
                    value: String(contact.id)
                  }))}
                  defaultValue={
                    armadoContactName
                      ? String(
                          externalData.contacts.find(
                            (contact) => contact.name === armadoContactName
                          )?.id
                        )
                      : String(
                          externalData.contacts.find(
                            (contact) => contact.id === externalData.contactId
                          )?.id
                        )
                  }
                  onChange={(value) => {
                    setArmadoContactName(
                      externalData.contacts?.find(
                        (contact) => contact.id === Number.parseInt(value, 10)
                      )?.name ?? 'Inconnu'
                    )
                    setShowContactDropdown(false)
                  }}
                  required
                />
              )}

            {externalData &&
              !externalData.externalQualificationId &&
              externalData.externalQualifications.length > 0 && (
                <Dropdown
                  label="Qualification"
                  name="accountingExternalQualificationId"
                  placeholder="Sélectionner une qualification"
                  options={externalData.externalQualifications.map(
                    (qualif) => ({
                      label: qualif.name,
                      value: String(qualif.id)
                    })
                  )}
                  required
                  searchable
                />
              )}

            {canSendOrder &&
              externalData &&
              accountingService === 'Armado' &&
              (metadata?.sendActivationEmail ? (
                <p>
                  Un email a été envoyé au contact pour activer son compte
                  Armado.
                </p>
              ) : (
                <Checkbox
                  label="Envoyer un email au contact pour activer son compte Armado"
                  name="accountingSendActivationMail"
                  defaultChecked={false}
                />
              ))}

            {canSendOrder &&
              externalData &&
              accountingService === 'Armado' &&
              (metadata?.warnClient ? (
                <p>La commande est partagée avec le contact.</p>
              ) : (
                <Checkbox
                  label="Partager la commande avec le contact"
                  name="accountingWarnClient"
                  defaultChecked={false}
                />
              ))}
          </div>
        )}
        {/** END OF Accounting form section */}
      </StandardModal>
    </Form>,
    document.querySelector('#appWrapper') as HTMLElement
  )
}
